import React, { createRef, useCallback, useEffect, useMemo, useRef, useState, createContext, Fragment } from 'react';

import { Box } from '@chakra-ui/react';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { isMobileView } from 'constants/isMobileView';

import { setCurrentSectionInView } from '../KRslice';

const ScrollableSectionNavContext = createContext({});

const NavContainer = ({ boxContainerStyles = {}, navItems = [], selectedNavItem, onNavItemClick }) => (
  <Box {...boxContainerStyles}>
    {navItems.map(({ renderNavItem, id }) => (
      <Fragment key={id}>
        {renderNavItem({
          selected: id === selectedNavItem,
          onClick: (ev) => onNavItemClick(ev, id),
        })}
      </Fragment>
    ))}
  </Box>
);

const SectionContentWithRef = React.memo(
  ({ sectionRef, id, SectionContent, setInViewSections, localChangedNameOKR }) => {
    const { ref: inViewRef, inView } = useInView({ threshold: 0.5 });

    useEffect(() => {
      setInViewSections((prev) => {
        const visibleSections = prev.map((section) => {
          if (section.id === id) {
            return { ...section, inView };
          }
          return { ...section };
        });
        return visibleSections;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView]);

    const setRefs = useCallback(
      (node) => {
        // eslint-disable-next-line no-param-reassign
        sectionRef.current = node;
        // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
        inViewRef(node);
      },
      [inViewRef, sectionRef]
    );
    return <SectionContent ref={setRefs} inView={inView} localChangedNameOKR={localChangedNameOKR} />;
  }
);

const SectionContainer = ({ boxContainerStyles = {}, sections, setInViewSections }) => {
  const mobileViewProps = isMobileView() ? { overflowX: 'hidden' } : {};
  return (
    <Box {...boxContainerStyles} id="section-container-primary-panel" {...mobileViewProps}>
      {sections.map(({ ref, SectionContent, id, localChangedNameOKR = undefined }) => (
        <Fragment key={id}>
          <SectionContentWithRef
            sectionRef={ref}
            SectionContent={SectionContent}
            id={id}
            setInViewSections={setInViewSections}
            localChangedNameOKR={localChangedNameOKR}
          />
        </Fragment>
      ))}
    </Box>
  );
};

const ScrollableSectionNavV2 = ({
  options,
  navContainerStyles = {},
  sectionContainerStyles = {},
  isHideNavBarWhenSingleSection = false,
}) => {
  const location = useLocation();
  const history = useHistory();

  const previousSectionsRef = useRef([]);
  const [navItems, sections] = useMemo(() => {
    const previousSections = previousSectionsRef.current;
    const newNavItems = options.map(({ id, renderNavItem }) => ({ id, renderNavItem }));

    const newSections = options.map(({ id, SectionContent, localChangedNameOKR }) => {
      let previousSection = previousSections.find(({ id: _id }) => id === _id);
      if (previousSection) {
        previousSection = { ...previousSection, localChangedNameOKR };
      }

      /* @vikalp-sajwan reverting this change as it is causing issue with okr bottom drawer.
       * https://linear.app/peoplemesh/issue/ENG-12843/unable-to-scroll-to-comment-section-in-okr-bottom-drawer
       */
      return previousSection ?? { id, SectionContent, ref: createRef(), localChangedNameOKR };
    });

    previousSectionsRef.current = newSections;

    return [newNavItems, newSections];
  }, [options]);

  const dispatch = useDispatch();

  const [selectedNavItem, setSelectedNavItem] = useState(navItems?.[0]?.id);
  const [isAutoScrollActive, setIsAutoScrollActive] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const [inViewSections, setInViewSections] = useState(
    navItems.map((navItem) => ({ id: navItem?.id, inView: false }) || [])
  );

  const scrollToComponentTop = useCallback(
    (ref, lastClickedNavItemId) => {
      if (!ref) return;
      ref.current?.scrollIntoView({
        behavior: 'smooth',
      });
      if (scrollTimeout) clearTimeout(scrollTimeout);
      setScrollTimeout(
        setTimeout(() => {
          setSelectedNavItem(lastClickedNavItemId);
          setIsAutoScrollActive(false);
        }, [1000])
      );
    },
    [scrollTimeout, setScrollTimeout, setSelectedNavItem, setIsAutoScrollActive]
  );

  useEffect(() => {
    if (inViewSections.length && !isAutoScrollActive) {
      const firstVisibleSection = inViewSections.find((section) => section.inView === true);
      if (firstVisibleSection && firstVisibleSection.id !== selectedNavItem) {
        setSelectedNavItem(firstVisibleSection.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewSections]);

  useEffect(() => {
    dispatch(setCurrentSectionInView(selectedNavItem));
  }, [dispatch, selectedNavItem]);

  const onNavItemClick = useCallback(
    (ev, id) => {
      setSelectedNavItem(id);
      setIsAutoScrollActive(true);
      scrollToComponentTop(sections?.find(({ id: _id }) => id === _id)?.ref, id);
    },
    [scrollToComponentTop, sections]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const scrollToId = queryParams.get('scrollToId');
    if (scrollToId && scrollToId !== selectedNavItem) {
      // timeout is added to make sure that the section is rendered before scrolling to it
      setTimeout(() => onNavItemClick(null, scrollToId), 500);
      queryParams.delete('scrollToId');
      history.replace({ search: queryParams.toString() });
    }
  }, [onNavItemClick, location, selectedNavItem, history]);

  useEffect(() => {
    if (navItems.findIndex(({ id }) => id === selectedNavItem) === -1) setSelectedNavItem(navItems?.[0]?.id);
  }, [navItems, selectedNavItem]);

  const hideSideNav = (isHideNavBarWhenSingleSection && sections.length <= 1) || isMobileView();
  const contextValue = useMemo(() => ({}), []);

  return (
    <ScrollableSectionNavContext.Provider value={contextValue}>
      {!hideSideNav && (
        <NavContainer
          boxContainerStyles={navContainerStyles}
          navItems={navItems}
          selectedNavItem={selectedNavItem}
          onNavItemClick={onNavItemClick}
        />
      )}
      <SectionContainer
        boxContainerStyles={sectionContainerStyles}
        sections={sections}
        setInViewSections={setInViewSections}
      />
    </ScrollableSectionNavContext.Provider>
  );
};

export default ScrollableSectionNavV2;
